import React, { useRef, useCallback, useEffect } from 'react';
import {
  Bold, Italic, Underline, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Type, Undo, Redo
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  readOnly?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  minHeight = '300px',
  readOnly = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);

  // Sync external value changes
  useEffect(() => {
    if (editorRef.current && !isInternalUpdate.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalUpdate.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalUpdate.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCmd = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    handleInput();
  };

  const toolbarButtons = [
    { icon: Bold, cmd: 'bold', title: 'Bold (Ctrl+B)' },
    { icon: Italic, cmd: 'italic', title: 'Italic (Ctrl+I)' },
    { icon: Underline, cmd: 'underline', title: 'Underline (Ctrl+U)' },
    { divider: true },
    { icon: List, cmd: 'insertUnorderedList', title: 'Bullet List' },
    { icon: ListOrdered, cmd: 'insertOrderedList', title: 'Numbered List' },
    { divider: true },
    { icon: AlignLeft, cmd: 'justifyLeft', title: 'Align Left' },
    { icon: AlignCenter, cmd: 'justifyCenter', title: 'Align Center' },
    { icon: AlignRight, cmd: 'justifyRight', title: 'Align Right' },
    { divider: true },
    { icon: Undo, cmd: 'undo', title: 'Undo' },
    { icon: Redo, cmd: 'redo', title: 'Redo' },
  ];

  return (
    <div style={{
      border: '1px solid #334155',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: '#0f172a'
    }}>
      {/* Toolbar */}
      {!readOnly && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '2px', padding: '8px 12px',
          borderBottom: '1px solid #334155', backgroundColor: '#1e293b',
          flexWrap: 'wrap'
        }}>
          {/* Heading select */}
          <select
            onChange={(e) => {
              if (e.target.value === 'p') {
                execCmd('formatBlock', 'p');
              } else {
                execCmd('formatBlock', e.target.value);
              }
              e.target.value = '';
            }}
            defaultValue=""
            style={{
              padding: '4px 8px', backgroundColor: '#0f172a', border: '1px solid #475569',
              borderRadius: '4px', color: '#e2e8f0', fontSize: '12px', marginRight: '8px',
              cursor: 'pointer'
            }}
          >
            <option value="" disabled>Format</option>
            <option value="p">Paragraph</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
          </select>

          {toolbarButtons.map((btn, i) => {
            if ('divider' in btn) {
              return <div key={i} style={{ width: '1px', height: '20px', backgroundColor: '#475569', margin: '0 4px' }} />;
            }
            const Icon = btn.icon!;
            return (
              <button
                key={btn.cmd}
                type="button"
                onClick={() => execCmd(btn.cmd!)}
                title={btn.title}
                style={{
                  padding: '6px', background: 'none', border: 'none', borderRadius: '4px',
                  color: '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#334155'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icon size={16} />
              </button>
            );
          })}
        </div>
      )}

      {/* Editor Area */}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder}
        style={{
          minHeight,
          padding: '16px',
          color: '#e2e8f0',
          fontSize: '14px',
          lineHeight: '1.7',
          outline: 'none',
          overflowY: 'auto',
          maxHeight: '500px',
          cursor: readOnly ? 'default' : 'text'
        }}
      />

      <style>{`
        [data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #64748b;
          pointer-events: none;
        }
        [contenteditable] h2 { font-size: 20px; font-weight: 600; margin: 16px 0 8px 0; color: #f8fafc; }
        [contenteditable] h3 { font-size: 17px; font-weight: 600; margin: 12px 0 6px 0; color: #f8fafc; }
        [contenteditable] h4 { font-size: 15px; font-weight: 600; margin: 10px 0 4px 0; color: #f8fafc; }
        [contenteditable] p { margin: 0 0 8px 0; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 24px; margin: 8px 0; }
        [contenteditable] li { margin: 4px 0; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
